// Trang cấu hình mã khuyến mãi duyệt tự động
import React, { useState, useEffect } from 'react';
import { Card, Table, Input, Button, message, Space, Popconfirm } from 'antd';
import { ThunderboltOutlined, PlusOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';
import api, { errorMessage } from '../api';

const API_BASE = '/auto-approve';

function formatDate(str) {
  if (!str) return '-';
  const d = new Date(str);
  return d.toLocaleString('vi-VN');
}

// Trang xem những đơn đã duyệt tự động (export dùng trong App.js)
export function AutoApproveHistory() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');

  const fetchList = async (page = 1, pageSize = 20, searchTerm = '') => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (searchTerm) params.search = searchTerm;
      const res = await api.get(`${API_BASE}/log`, { params });
      if (res.data?.status && Array.isArray(res.data.data)) {
        setList(res.data.data);
        if (res.data.pagination) setPagination(res.data.pagination);
      } else {
        message.error(res.data?.message || 'Không lấy được danh sách');
      }
    } catch (err) {
      message.error(errorMessage(err, 'Lỗi kết nối API'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1, pagination.pageSize, search);
  }, []);

  const onSearch = () => {
    fetchList(1, pagination.pageSize, search);
  };

  const columns = [
    { title: 'STT', key: 'stt', width: 60, render: (_, __, i) => (pagination.page - 1) * pagination.pageSize + i + 1 },
    { title: 'ID đơn', dataIndex: 'order_id', key: 'order_id', width: 80 },
    { title: 'Tài khoản', dataIndex: 'account', key: 'account', width: 120 },
    { title: 'Họ tên', dataIndex: 'full_name', key: 'full_name', ellipsis: true },
    { title: 'Mã KM', dataIndex: 'promo_code', key: 'promo_code', width: 90 },
    { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120, ellipsis: true, render: (v) => v || '-' },
    { title: 'Tiền nạp', dataIndex: 'deposit_amount', key: 'deposit_amount', width: 100, align: 'right', render: (v) => (v != null ? Number(v).toLocaleString() : '-') },
    { title: 'Tiền thưởng', dataIndex: 'bonus_amount', key: 'bonus_amount', width: 100, align: 'right', render: (v) => (v != null ? Number(v).toLocaleString() : '-') },
    { title: 'Vòng cược', dataIndex: 'audit_amount', key: 'audit_amount', width: 100, align: 'right', render: (v) => (v != null ? Number(v).toLocaleString() : '-') },
    { title: 'Duyệt bởi', dataIndex: 'approved_by', key: 'approved_by', width: 100 },
    { title: 'Thời gian', dataIndex: 'approved_at', key: 'approved_at', width: 160, render: (v) => formatDate(v) },
  ];

  return (
    <Card title={<span><HistoryOutlined style={{ marginRight: 8 }} />Những đơn đã duyệt tự động</span>} style={{ background: '#FFF5EE' }}>
      <p style={{ color: '#666', marginBottom: 8 }}>Lịch sử các đơn được hệ thống <strong>duyệt tự động</strong> theo mã khuyến mãi đã cấu hình. Mỗi tài khoản chỉ được duyệt tự động 1 lần trong ngày.</p>
      <p style={{ marginBottom: 16, fontSize: 15 }}>
        <strong>Tổng số đơn đã duyệt tự động:</strong> <span style={{ color: '#1890ff', fontWeight: 600 }}>{pagination.total}</span> đơn
        {search && ` (đang lọc)`}
      </p>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input.Search
          placeholder="Tìm theo tài khoản, họ tên, mã KM..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={onSearch}
          style={{ width: 320 }}
          allowClear
        />
        <Button type="primary" onClick={onSearch}>Tìm kiếm</Button>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        size="small"
        locale={{ emptyText: loading ? 'Đang tải...' : 'Chưa có đơn nào được duyệt tự động' }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (t) => `Tổng ${t} đơn`,
          onChange: (page, pageSize) => fetchList(page, pageSize, search),
        }}
      />
    </Card>
  );
}

function AutoApprove() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get(API_BASE);
      if (res.data?.status && Array.isArray(res.data.data)) {
        setList(res.data.data);
      } else {
        message.error(res.data?.message || 'Không lấy được danh sách');
      }
    } catch (err) {
      message.error(errorMessage(err, 'Lỗi kết nối API'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleAdd = async () => {
    const code = (promoCode || '').trim().toUpperCase();
    if (!code) {
      message.warning('Vui lòng nhập mã khuyến mãi');
      return;
    }
    setAddLoading(true);
    try {
      const res = await api.post(API_BASE, { promo_code: code });
      if (res.data?.status) {
        message.success('Đã thêm mã duyệt tự động');
        if (res.data.warning) message.warning(res.data.warning, 5);
        setPromoCode('');
        fetchList();
      } else {
        message.error(res.data?.message || 'Thêm thất bại');
      }
    } catch (err) {
      message.error(errorMessage(err, 'Lỗi kết nối API'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await api.delete(API_BASE, { params: { id: record.id } });
      if (res.data?.status) {
        message.success('Đã xóa khỏi danh sách duyệt tự động');
        fetchList();
      } else {
        message.error(res.data?.message || 'Xóa thất bại');
      }
    } catch (err) {
      message.error(errorMessage(err, 'Lỗi kết nối API'));
    }
  };

  const columns = [
    { title: 'STT', key: 'stt', width: 70, render: (_, __, i) => i + 1 },
    { title: 'Mã khuyến mãi', dataIndex: 'promo_code', key: 'promo_code' },
    {
      title: 'Thao tác',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Popconfirm
          title="Xóa mã này khỏi danh sách duyệt tự động?"
          onConfirm={() => handleDelete(record)}
          okText="Xóa"
          cancelText="Hủy"
        >
          <Button type="link" danger size="small" icon={<DeleteOutlined />}>
            Xóa
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <ThunderboltOutlined />
          Đơn duyệt tự động
        </Space>
      }
      style={{ background: '#FFF5EE' }}
    >
      <p style={{ color: '#666', marginBottom: 16 }}>
        Các mã khuyến mãi trong danh sách sẽ được <strong>duyệt tự động</strong> khi có đơn chờ phê duyệt trùng mã (khi mở trang &quot;Đơn chờ phê duyệt&quot; hoặc khi danh sách tự làm mới).
      </p>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Nhập mã khuyến mãi (vd: GK01)"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          onPressEnter={handleAdd}
          style={{ width: 220 }}
          allowClear
        />
        <Button type="primary" icon={<PlusOutlined />} loading={addLoading} onClick={handleAdd}>
          Thêm mã
        </Button>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={false}
        size="small"
      />
    </Card>
  );
}

export default AutoApprove;
